import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Crosshair, Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Auth() {
  const [params] = useSearchParams();
  const { authSuccess, user } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "buyer" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (user) nav("/dashboard"); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const path = mode === "register" ? "/auth/register" : "/auth/login";
      const payload = mode === "register" ? form : { email: form.email, password: form.password };
      const { data } = await api.post(path, payload);
      authSuccess(data);
      toast.success(mode === "register" ? "Welcome to the desk." : "Locked and loaded.");
      nav("/dashboard");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="relative z-10 min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex flex-col items-center justify-center relative border-r border-[#5A2A24] p-12 overflow-hidden">
        <div className="absolute inset-0 tac-grid opacity-30" />
        <img src="https://customer-assets-39nsmqrw.emergentagent.net/job_gunbri-broker/artifacts/egembtay_image.png"
             alt="Shooter Bazaar" className="relative w-full max-w-md object-contain" />
        <p className="mono-label mt-8 relative">Curated iron · Live offers · Compliant transfers</p>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12 pt-28">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-10">
            <Crosshair className="w-5 h-5 text-[#C0894F]" />
            <span className="font-display text-xl">SHOOTER <span className="text-[#C0894F]">BAZAAR</span></span>
          </Link>

          <div className="flex border border-[#5A2A24] mb-8">
            {["login", "register"].map((m) => (
              <button key={m} onClick={() => { setMode(m); setError(""); }} data-testid={`tab-${m}`}
                className={`flex-1 h-11 font-mono text-xs uppercase tracking-widest transition-colors ${mode === m ? "bg-[#C0894F] text-white" : "text-[#C7A98F] hover:text-[#EFE4D2]"}`}>
                {m === "login" ? "Sign In" : "Register"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="mono-label block mb-1">Full Name</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="auth-name" className="field" />
              </div>
            )}
            <div>
              <label className="mono-label block mb-1">Email</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="auth-email" className="field" />
            </div>
            <div>
              <label className="mono-label block mb-1">Password</label>
              <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="auth-password" className="field" />
            </div>
            {mode === "register" && (
              <div>
                <label className="mono-label block mb-2">I want to</label>
                <div className="grid grid-cols-2 gap-2">
                  {[["buyer", "Buy Iron"], ["seller", "Sell Iron"]].map(([v, l]) => (
                    <button type="button" key={v} onClick={() => setForm({ ...form, role: v })} data-testid={`role-${v}`}
                      className={`h-11 font-mono text-xs uppercase tracking-widest border transition-colors ${form.role === v ? "border-[#C0894F] text-[#C0894F]" : "border-[#5A2A24] text-[#C7A98F] hover:border-[#EFE4D2]"}`}>{l}</button>
                  ))}
                </div>
              </div>
            )}
            {error && <div className="text-[#C0894F] font-mono text-xs" data-testid="auth-error">{error}</div>}
            <button type="submit" disabled={loading} data-testid="auth-submit" className="btn-tactical w-full disabled:opacity-50">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
